import type { ReactNode } from 'react';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import { SectionLabel } from './SectionLabel';
import { CategoryPills, type CategoryPill } from './CategoryPills';
import { ScrollReveal } from './ScrollReveal';

export interface DiscoverHeaderProps {
    /** Eyebrow text, e.g. "(01 – Projects)". */
    eyebrow: string;
    /** Giant display heading, e.g. "Discover Projects". */
    heading: string;
    categoriesLabel?: string;
    categories: CategoryPill[];
    description: string;
    ctaLabel?: string;
    ctaTo?: string;
    /** Override the CTA pill classes (e.g. the shared detail-page CTA style). */
    ctaClassName?: string;
    /** Renders in place of the CTA pill (e.g. a subscribe field) when provided. */
    actionSlot?: ReactNode;
    /** Extra classes for the display heading (e.g. a tighter tracking override). */
    headingClassName?: string;
    /** Extra classes for the popular-categories column (e.g. a gap override). */
    categoriesClassName?: string;
    /**
     * Extra classes for the header's own block — a ground, for a page whose
     * light is not white (the profile's is `#f5f5f7`), or spacing a host
     * section owns.
     */
    className?: string;
    /** dark = black section / white type; light = white section / dark type. */
    theme: 'dark' | 'light';
    /** When true the separator rule spans the full width (eyebrow stays right). */
    fullWidthRule?: boolean;
    /** Drop the header's top padding so a page-level spacer owns the gap above
     *  it (professional-detail sections already sit under a SectionSpacer). */
    flushTop?: boolean;
    /** Stack the categories above the description on mobile (instead of the
     *  38% / rest split) — needed when category names run long and would
     *  otherwise overflow the narrow column. */
    stackOnMobile?: boolean;
    /** Mobile eyebrow sizing: `rem` (detail-page sections) snaps it to the
     *  shared 14px token; `vw` (homepage) keeps the fluid size. Desktop sizes
     *  are rem-anchored from lg in both modes. */
    unit?: 'vw' | 'rem';
}

/**
 * Shared editorial header for the Discover sections (Projects, Professionals,
 * Articles): a hairline + eyebrow, a giant display heading, then a
 * popular-categories column beside a description + CTA pill. Sized in rem so
 * it scales with the root font below 1920 and holds the design px above.
 */
export const DiscoverHeader = ({
    eyebrow,
    heading,
    categoriesLabel = 'Popular categories:',
    categories,
    description,
    ctaLabel,
    ctaTo,
    ctaClassName,
    actionSlot,
    headingClassName,
    categoriesClassName,
    className,
    theme,
    fullWidthRule = false,
    flushTop = false,
    stackOnMobile = false,
    unit = 'vw',
}: DiscoverHeaderProps) => {
    const isDark = theme === 'dark';
    const isRem = unit === 'rem';
    return (
        <div
            className={cn(
                'mx-auto flex w-full max-w-[120rem] flex-col max-md:gap-8 max-md:px-6 max-md:pt-10 md:px-[1.927vw] lg:px-[2.3124rem]',
                flushTop ? '' : 'md:pt-[8.3333vw] lg:pt-[10rem]',
                'md:gap-[13.5417vw] lg:gap-[16.25rem]',
                isDark
                    ? 'bg-black text-white focus-ring-on-dark'
                    : 'bg-white text-[#181818] focus-ring-on-light',
                className,
            )}
        >
            {/* Design tightens the eyebrow→heading gap on the full-width-rule
                headers (Professionals/Articles/Magazine, 92px) vs Projects (190px).
                Each element reveals in sequence, matching the Home Design Evening
                cadence (eyebrow → heading → body, ~120ms apart). */}
            <div
                className={cn(
                    'flex flex-col max-md:gap-4',
                    fullWidthRule
                        ? 'md:gap-[4.7917vw] lg:gap-[5.75rem]'
                        : 'md:gap-[9.9vw] lg:gap-[11.88rem]',
                )}
            >
                <ScrollReveal>
                    <div className="flex w-full flex-col max-md:gap-4 md:gap-[0.885vw] lg:gap-[1.062rem]">
                        <div
                            className={cn(
                                'h-px',
                                fullWidthRule
                                    ? 'w-full'
                                    : 'w-full md:ml-auto md:w-[47.5vw] lg:w-[57rem]',
                                isDark ? 'bg-white/40' : 'bg-black/20',
                            )}
                        />
                        <div
                            className={cn(
                                'w-full',
                                !fullWidthRule &&
                                    'md:ml-auto md:w-[47.5vw] lg:w-[57rem]',
                            )}
                        >
                            <SectionLabel
                                className={cn(
                                    'tracking-[-0.01em] md:text-[1.6667vw] lg:text-[2rem]',
                                    // Detail-page sections (rem) snap the mobile
                                    // eyebrow to the shared 14px token; the
                                    // homepage keeps its fluid size.
                                    isRem
                                        ? 'max-md:text-[0.875rem]'
                                        : 'max-md:text-[0.4528rem]',
                                    isDark && 'text-white',
                                )}
                            >
                                {eyebrow}
                            </SectionLabel>
                        </div>
                    </div>
                </ScrollReveal>
                <ScrollReveal delay={120}>
                    <h2
                        className={cn(
                            'm-0 font-sans font-normal max-md:text-[3.0326rem] max-md:leading-[3.0998rem] max-md:tracking-[-0.1268rem] md:w-[70.4vw] md:text-[10.4167vw] md:leading-[9.375vw] lg:w-[84.48rem] lg:text-[12.5rem] lg:leading-[11.25rem]',
                            // Black-on-white headings read looser than white-on-black
                            // at the same value, so light sections track tighter.
                            isDark
                                ? 'md:tracking-[-0.3vw] lg:tracking-[-0.36rem]'
                                : 'md:tracking-[-0.4375vw] lg:tracking-[-0.525rem]',
                            headingClassName,
                        )}
                    >
                        {heading}
                    </h2>
                </ScrollReveal>
            </div>

            <ScrollReveal delay={240}>
                <div
                    className={cn(
                        'flex md:gap-[24vw] lg:gap-[28.8rem]',
                        stackOnMobile
                            ? 'max-md:flex-col max-md:gap-6'
                            : 'max-md:gap-5',
                    )}
                >
                    <CategoryPills
                        label={categoriesLabel}
                        items={categories}
                        className={cn(
                            stackOnMobile ? 'max-md:w-full' : 'max-md:w-[38%]',
                            'md:w-[24.3vw] lg:w-[29.16rem]',
                            categoriesClassName,
                        )}
                    />
                    <div
                        className={cn(
                            'flex min-w-0 flex-col max-md:gap-5 md:w-[43.96vw] md:gap-[3.49vw] lg:w-[52.752rem] lg:gap-[4.188rem]',
                            !stackOnMobile && 'max-md:flex-1',
                        )}
                    >
                        <p
                            className={cn(
                                'm-0 font-sans leading-[1.3] font-normal max-md:text-[0.8099rem] md:text-[2.0312vw] lg:text-[2.4375rem]',
                            )}
                        >
                            {description}
                        </p>
                        {actionSlot ??
                            (ctaLabel && ctaTo ? (
                                <RoccoLink
                                    to={ctaTo}
                                    className={
                                        ctaClassName ??
                                        cn(
                                            'inline-flex w-fit items-center justify-center rounded-[var(--radius-btn-l)] font-sans font-normal tracking-[-0.03em] whitespace-nowrap shadow-[0px_3.243px_11.351px_rgba(0,0,0,0.11)] max-md:rounded-[var(--radius-btn-m)] max-md:px-7 max-md:py-3 max-md:text-base md:h-[3.125vw] md:min-w-[11.99vw] md:px-[1.436vw] md:text-[1.182vw] lg:h-[3.75rem] lg:min-w-[14.388rem] lg:px-[1.7232rem] lg:text-[1.4184rem]',
                                            isDark
                                                ? 'bg-white text-black'
                                                : 'bg-[#181818] text-white',
                                        )
                                    }
                                >
                                    {ctaLabel}
                                </RoccoLink>
                            ) : null)}
                    </div>
                </div>
            </ScrollReveal>
        </div>
    );
};
